/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Edit Organization Page
 * ============================================================================
 *
 * Page for editing organization details using the real API.
 *
 * ============================================================================
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Save, Loader2, Scale, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useOrganization, useUpdateOrganization } from '@/hooks/api/useOrganizations';
import { useCurrencies } from '@/hooks/api/useCurrencies';
import { useNotification } from '@/hooks';
import { isApiError, getValidationErrors } from '@/services/api/errors';
import { UpdateOrganizationRequest } from '@/types/superadmin.types';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { countries, getCountryConfig, CountryConfig } from '@/data/countries';
import { useCountries } from '@/hooks/useCountries';

// Types
type CompanySize = '1-10' | '11-50' | '51-200' | '200+';

interface EditOrgForm {
  name: string;
  legalName: string;
  taxId: string;
  rccm: string;
  // OHADA Compliance fields
  placeOfRegistration: string;
  legalForm: string;
  capitalAmount: string;
  capitalCurrency: string;
  industry: string;
  companySize: CompanySize;
  primaryContactEmail: string;
  primaryContactPhone: string;
  primaryContactName: string;
  address: string;
  city: string;
  country: string;
  currency: string;
  notes: string;
}

// OHADA zone countries (ISO 3166-1 alpha-2)
const OHADA_ZONE_COUNTRIES = [
  'Benin', 'Burkina Faso', 'Cameroon', 'Central African Republic', 'Chad',
  'Comoros', 'Congo', 'Democratic Republic of the Congo', 'Equatorial Guinea',
  'Gabon', 'Guinea', 'Guinea-Bissau', 'Ivory Coast', 'Mali', 'Niger', 'Senegal', 'Togo'
];

const isOhadaZoneCountry = (country: string): boolean => {
  return OHADA_ZONE_COUNTRIES.some(c =>
    c.toLowerCase() === country.toLowerCase() ||
    country.toLowerCase().includes(c.toLowerCase())
  );
};

// Form Input Component
const FormInput: React.FC<{
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  placeholder?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  mono?: boolean;
}> = ({ label, name, value, onChange, type = 'text', placeholder, error, required, disabled, mono }) => (
  <div className="form-group">
    <label className="form-label">
      {label} {required && <span className="required">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      className={`form-input ${mono ? 'mono' : ''} ${error ? 'has-error' : ''}`}
    />
    {error && <span className="field-error">{error}</span>}
  </div>
);

// Form Select Component
const FormSelect: React.FC<{
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: { value: string; label: string }[];
  error?: string;
  help?: string;
}> = ({ label, name, value, onChange, options, error, help }) => (
  <div className="form-group">
    <label className="form-label">{label}</label>
    <select
      name={name}
      value={value}
      onChange={onChange}
      className={`form-select ${error ? 'has-error' : ''}`}
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
    {help && <div className="form-help">{help}</div>}
    {error && <span className="field-error">{error}</span>}
  </div>
);

// Main Edit Organization Page
export default function EditOrganizationPage() {
  const { t } = useTranslation('platform');
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error: showError } = useNotification();

  // Fetch organization data
  const { data: org, isLoading, error: fetchError } = useOrganization(id || '');

  // Update mutation
  const updateMutation = useUpdateOrganization();

  // Fetch currencies
  const { data: currencies = [] } = useCurrencies();
  const { countryOptions: localizedCountryOptions } = useCountries();

  // Form state
  const [form, setForm] = useState<EditOrgForm>({
    name: '',
    legalName: '',
    taxId: '',
    rccm: '',
    // OHADA Compliance fields
    placeOfRegistration: '',
    legalForm: '',
    capitalAmount: '',
    capitalCurrency: 'GNF',
    industry: '',
    companySize: '1-10',
    primaryContactEmail: '',
    primaryContactPhone: '',
    primaryContactName: '',
    address: '',
    city: '',
    country: 'Guinea',
    currency: 'USD',
    notes: '',
  });

  // Field errors from API
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Populate form when organization data loads
  useEffect(() => {
    if (org) {
      setForm({
        name: org.name || '',
        legalName: org.legalName || '',
        taxId: org.taxId || '',
        rccm: org.rccm || '',
        // OHADA Compliance fields
        placeOfRegistration: org.placeOfRegistration || '',
        legalForm: org.legalForm || '',
        capitalAmount: org.capitalAmount ? String(org.capitalAmount) : '',
        capitalCurrency: org.capitalCurrency || 'GNF',
        industry: org.industry || '',
        companySize: (org.companySize as CompanySize) || '1-10',
        primaryContactEmail: org.primaryContactEmail || '',
        primaryContactPhone: org.primaryContactPhone || '',
        primaryContactName: org.primaryContactName || '',
        address: org.address || '',
        city: org.city || '',
        country: org.country || 'Guinea',
        currency: org.currency || 'USD',
        notes: org.notes || '',
      });
    }
  }, [org]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setFieldErrors({});

    const updateData: UpdateOrganizationRequest = {
      name: form.name,
      legalName: form.legalName || undefined,
      taxId: form.taxId || undefined,
      rccm: form.rccm || undefined,
      // OHADA Compliance fields
      placeOfRegistration: form.placeOfRegistration || undefined,
      legalForm: form.legalForm || undefined,
      capitalAmount: form.capitalAmount ? parseFloat(form.capitalAmount) : undefined,
      capitalCurrency: form.capitalCurrency || undefined,
      industry: form.industry || undefined,
      companySize: form.companySize,
      primaryContactEmail: form.primaryContactEmail || undefined,
      primaryContactPhone: form.primaryContactPhone || undefined,
      primaryContactName: form.primaryContactName || undefined,
      address: form.address || undefined,
      city: form.city || undefined,
      country: form.country || undefined,
      currency: form.currency,
      notes: form.notes || undefined,
    };

    updateMutation.mutate(
      { id, data: updateData },
      {
        onSuccess: () => {
          success(t('editOrganization.messages.success'));
          navigate(`/superadmin/organizations/${id}`);
        },
        onError: (error) => {
          if (isApiError(error)) {
            const errors = getValidationErrors(error);
            if (errors) {
              setFieldErrors(errors);
            }
            if (error.status !== 409) {
              showError(error.message);
            }
          } else {
            showError(t('createOrganization.messages.unexpectedError'));
          }
        },
      }
    );
  };

  const industryOptions = useMemo(() => [
    { value: '', label: t('createOrganization.industry.select') },
    { value: 'retail', label: t('createOrganization.industry.retail') },
    { value: 'import-export', label: t('createOrganization.industry.importExport') },
    { value: 'manufacturing', label: t('createOrganization.industry.manufacturing') },
    { value: 'technology', label: t('createOrganization.industry.technology') },
    { value: 'services', label: t('createOrganization.industry.services') },
    { value: 'finance', label: t('createOrganization.industry.finance') },
    { value: 'healthcare', label: t('createOrganization.industry.healthcare') },
    { value: 'education', label: t('createOrganization.industry.education') },
    { value: 'other', label: t('createOrganization.industry.other') },
  ], [t]);

  const companySizeOptions = useMemo(() => [
    { value: '1-10', label: t('createOrganization.companySize.1-10') },
    { value: '11-50', label: t('createOrganization.companySize.11-50') },
    { value: '51-200', label: t('createOrganization.companySize.51-200') },
    { value: '200+', label: t('createOrganization.companySize.200+') },
  ], [t]);

  const currencyOptions = useMemo(() =>
    currencies.map(c => ({
      value: c.code,
      label: `${c.code} - ${c.name} (${c.symbol})`
    })),
  [currencies]);

  // OHADA Legal Form options
  const legalFormOptions = useMemo(() => [
    { value: '', label: t('editOrganization.ohadaCompliance.selectLegalForm') },
    { value: 'SARL', label: t('editOrganization.ohadaCompliance.legalForms.SARL') },
    { value: 'SA', label: t('editOrganization.ohadaCompliance.legalForms.SA') },
    { value: 'SAS', label: t('editOrganization.ohadaCompliance.legalForms.SAS') },
    { value: 'SNC', label: t('editOrganization.ohadaCompliance.legalForms.SNC') },
    { value: 'SCS', label: t('editOrganization.ohadaCompliance.legalForms.SCS') },
    { value: 'GIE', label: t('editOrganization.ohadaCompliance.legalForms.GIE') },
    { value: 'EI', label: t('editOrganization.ohadaCompliance.legalForms.EI') },
    { value: 'SUARL', label: t('editOrganization.ohadaCompliance.legalForms.SUARL') },
  ], [t]);

  // Check if current country is in OHADA zone
  const showOhadaSection = useMemo(() => isOhadaZoneCountry(form.country), [form.country]);

  const countryOptions = localizedCountryOptions;

  // Get country config based on selected country (for dynamic placeholders)
  const selectedCountryConfig = useMemo((): CountryConfig | null => {
    const selectedCountry = countries.find(c => c.name === form.country);
    if (selectedCountry) {
      return getCountryConfig(selectedCountry.code);
    }
    return null;
  }, [form.country]);

  // ISO code for the selected country (for PhoneInput flag)
  const selectedCountryCode = useMemo(() => {
    const selectedCountry = countries.find(c => c.name === form.country);
    return selectedCountry?.code || 'GN';
  }, [form.country]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (fetchError || !org) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted">
        <Building2 className="w-12 h-12 mb-3 opacity-50" />
        <p>{fetchError ? `${t('common.error')}: ${fetchError.message}` : t('organizationDetail.notFound')}</p>
        <button
          onClick={() => navigate('/superadmin/organizations')}
          className="mt-4 px-4 py-2 bg-primary text-white rounded-lg text-sm"
        >
          {t('editOrganization.backToOrganizations')}
        </button>
      </div>
    );
  }

  return (
    <div className="edit-org-page">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/superadmin/organizations/${id}`)}
            className="p-2 rounded-lg border border-border hover:bg-background transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="page-title">
              <Building2 className="w-5 h-5 inline mr-2" />
              {t('editOrganization.title', { name: org.name })}
            </h1>
            <p className="page-subtitle">{org.code} - {t('editOrganization.subtitle')}</p>
          </div>
        </div>
        <div className="page-actions">
          <button
            type="button"
            onClick={() => navigate(`/superadmin/organizations/${id}`)}
            className="btn btn-secondary"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            form="edit-org-form"
            disabled={updateMutation.isPending || !form.name}
            className="btn btn-primary"
          >
            {updateMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            <span className="btn-text">{t('editOrganization.saveChanges')}</span>
          </button>
        </div>
      </div>

      <form id="edit-org-form" onSubmit={handleSubmit}>
        {/* Organization Information */}
        <div className="form-section">
          <div className="form-section-title">{t('createOrganization.sections.organization')}</div>
          <div className="form-grid">
            <FormInput
              label={t('createOrganization.fields.organizationName')}
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder={t('createOrganization.placeholders.organizationName')}
              required
              error={fieldErrors.name}
            />
            <FormInput
              label={t('organizationDetail.fields.legalName')}
              name="legalName"
              value={form.legalName}
              onChange={handleChange}
              placeholder={t('createOrganization.placeholders.legalName')}
              error={fieldErrors.legalName}
            />
            <FormInput
              label={selectedCountryConfig?.taxIdLabel || t('organizationDetail.fields.taxId')}
              name="taxId"
              value={form.taxId}
              onChange={handleChange}
              placeholder={selectedCountryConfig?.taxIdPlaceholder || 'XXXX-XXXXXXXX'}
              mono
              error={fieldErrors.taxId}
            />
            {selectedCountryConfig?.businessRegistryLabel && (
              <FormInput
                label={selectedCountryConfig.businessRegistryLabel}
                name="rccm"
                value={form.rccm}
                onChange={handleChange}
                placeholder={selectedCountryConfig.businessRegistryPlaceholder || ''}
                mono
                error={fieldErrors.rccm}
              />
            )}
            <FormSelect
              label={t('organizationDetail.fields.industry')}
              name="industry"
              value={form.industry}
              onChange={handleChange}
              options={industryOptions}
              error={fieldErrors.industry}
            />
            <FormSelect
              label={t('createOrganization.fields.companySize')}
              name="companySize"
              value={form.companySize}
              onChange={handleChange}
              options={companySizeOptions}
              error={fieldErrors.companySize}
            />
            <FormSelect
              label={t('createOrganization.fields.currency')}
              name="currency"
              value={form.currency}
              onChange={handleChange}
              options={currencyOptions}
              error={fieldErrors.currency}
              help={t('createOrganization.help.currencyNote')}
            />
          </div>
        </div>

        {/* OHADA Compliance Section - Only shown for OHADA zone countries */}
        {showOhadaSection && (
          <div className="form-section ohada-section">
            <div className="form-section-title flex items-center gap-2">
              <Scale className="w-5 h-5" />
              {t('editOrganization.ohadaCompliance.title')}
            </div>
            <div className="form-section-subtitle flex items-center gap-2 mb-4">
              <AlertCircle className="w-4 h-4 text-amber-500" />
              <span className="text-sm text-muted">{t('editOrganization.ohadaCompliance.subtitle')}</span>
            </div>
            <div className="form-grid">
              <FormInput
                label={t('editOrganization.ohadaCompliance.fields.placeOfRegistration')}
                name="placeOfRegistration"
                value={form.placeOfRegistration}
                onChange={handleChange}
                placeholder={t('editOrganization.ohadaCompliance.placeholders.placeOfRegistration')}
                error={fieldErrors.placeOfRegistration}
              />
              <FormSelect
                label={t('editOrganization.ohadaCompliance.fields.legalForm')}
                name="legalForm"
                value={form.legalForm}
                onChange={handleChange}
                options={legalFormOptions}
                error={fieldErrors.legalForm}
              />
              <FormInput
                label={t('editOrganization.ohadaCompliance.fields.capitalAmount')}
                name="capitalAmount"
                value={form.capitalAmount}
                onChange={handleChange}
                type="number"
                placeholder={t('editOrganization.ohadaCompliance.placeholders.capitalAmount')}
                error={fieldErrors.capitalAmount}
              />
              <FormSelect
                label={t('editOrganization.ohadaCompliance.fields.capitalCurrency')}
                name="capitalCurrency"
                value={form.capitalCurrency}
                onChange={handleChange}
                options={currencyOptions}
                error={fieldErrors.capitalCurrency}
              />
            </div>
          </div>
        )}

        {/* Contact Information */}
        <div className="form-section">
          <div className="form-section-title">{t('editOrganization.sections.contact')}</div>
          <div className="form-grid">
            <FormInput
              label={t('editOrganization.fields.contactName')}
              name="primaryContactName"
              value={form.primaryContactName}
              onChange={handleChange}
              placeholder={t('editOrganization.placeholders.contactName')}
              error={fieldErrors.primaryContactName}
            />
            <FormInput
              label={t('createOrganization.fields.emailAddress')}
              name="primaryContactEmail"
              value={form.primaryContactEmail}
              onChange={handleChange}
              type="email"
              placeholder={t('createOrganization.placeholders.email')}
              error={fieldErrors.primaryContactEmail || fieldErrors.email}
            />
            <PhoneInput
              label={t('createOrganization.fields.phoneNumber')}
              mask={selectedCountryConfig?.phonePlaceholder || '+XXX XXX XXX XXX'}
              countryCode={selectedCountryCode}
              value={form.primaryContactPhone}
              onChange={(v) => setForm(prev => ({ ...prev, primaryContactPhone: v }))}
              error={fieldErrors.primaryContactPhone}
              name="primaryContactPhone"
            />
            <FormInput
              label={t('editOrganization.fields.address')}
              name="address"
              value={form.address}
              onChange={handleChange}
              placeholder={t('editOrganization.placeholders.address')}
              error={fieldErrors.address}
            />
            <FormInput
              label={t('editOrganization.fields.city')}
              name="city"
              value={form.city}
              onChange={handleChange}
              placeholder={selectedCountryConfig?.capital || t('editOrganization.placeholders.city')}
              error={fieldErrors.city}
            />
            <div className="form-group">
              <label className="form-label">{t('editOrganization.fields.country')}</label>
              <SearchableSelect
                options={countryOptions}
                value={form.country}
                onChange={(value) => setForm(prev => ({ ...prev, country: value }))}
                placeholder={t('createOrganization.placeholders.selectCountry')}
                searchPlaceholder={t('createOrganization.placeholders.searchCountry')}
                clearable
              />
              {fieldErrors.country && <span className="field-error">{fieldErrors.country}</span>}
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="form-section">
          <div className="form-section-title">{t('editOrganization.sections.notes')}</div>
          <div className="form-group">
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows={3}
              className="form-textarea"
              placeholder={t('editOrganization.placeholders.notes')}
            />
          </div>
        </div>
      </form>

      <style>{styles}</style>
    </div>
  );
}

const styles = `
  .edit-org-page {
    width: 100%;
  }

  /* Page Header */
  .page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 24px;
    gap: 16px;
    flex-wrap: wrap;
  }

  .page-title {
    font-family: var(--font-heading);
    font-size: 22px;
    font-weight: 700;
    color: var(--color-text-primary);
    display: flex;
    align-items: center;
  }

  .page-subtitle {
    font-size: 13px;
    color: var(--color-text-secondary);
  }

  .page-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    .page-header {
      flex-direction: column;
      align-items: stretch;
    }

    .page-header > .flex {
      flex-wrap: wrap;
    }

    .page-title {
      font-size: 18px;
    }

    .page-actions {
      width: 100%;
      justify-content: flex-end;
    }

    .page-actions .btn {
      padding: 8px 12px;
      font-size: 12px;
    }

    .page-actions .btn-primary .btn-text {
      display: none;
    }
  }

  /* Buttons */
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 500;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .btn-primary {
    background: var(--color-brand-primary);
    color: white;
  }

  .btn-primary:hover:not(:disabled) {
    background: var(--color-brand-primary-hover);
  }

  .btn-secondary {
    background: transparent;
    color: var(--color-text-primary);
    border: 1px solid var(--color-border-default);
  }

  .btn-secondary:hover:not(:disabled) {
    background: var(--color-bg-hover);
  }

  /* Form Sections */
  .form-section {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 16px;
  }

  .form-section-title {
    font-family: var(--font-heading);
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 16px;
    color: var(--color-text-primary);
  }

  /* Form Grid */
  .form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  @media (max-width: 768px) {
    .form-grid {
      grid-template-columns: 1fr;
    }
  }

  /* Form Group */
  .form-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .form-label {
    font-size: 11px;
    font-weight: 500;
    color: var(--color-text-secondary);
  }

  .required {
    color: var(--color-error, #EF4444);
  }

  .form-input,
  .form-select,
  .form-textarea {
    width: 100%;
    padding: 0 12px;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-default);
    border-radius: 6px;
    font-size: 13px;
    color: var(--color-text-primary);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  .form-input,
  .form-select {
    height: 38px;
  }

  .form-textarea {
    padding: 12px;
    resize: vertical;
    min-height: 80px;
  }

  .form-input:focus,
  .form-select:focus,
  .form-textarea:focus {
    outline: none;
    border-color: var(--color-brand-primary);
    box-shadow: 0 0 0 2px rgba(var(--color-brand-primary-rgb, 139, 0, 0), 0.15);
  }

  .form-input.mono {
    font-family: var(--font-mono);
  }

  .form-help {
    font-size: 11px;
    color: var(--color-text-muted);
    margin-top: 4px;
  }

  /* Field error states */
  .form-input.has-error,
  .form-select.has-error {
    border-color: var(--color-error, #EF4444);
    background: rgba(239, 68, 68, 0.05);
  }

  .form-input.has-error:focus,
  .form-select.has-error:focus {
    border-color: var(--color-error, #EF4444);
    box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.15);
  }

  .field-error {
    display: block;
    font-size: 11px;
    color: var(--color-error, #EF4444);
    margin-top: 4px;
    animation: fadeIn 0.2s ease-out;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;
